import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Users } from 'lucide-react';
import { Button, TableHeader, CardStats, ConfirmModal, SearchInput, Pagination, ExportDropdown } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { usePagination, useExportExcel } from '@hooks';
import { getCustomersByConstructionId, getCustomerStats } from '@mocks/musteriler';
import MusterilerTablosu from './MusterilerTablosu';
import MusteriModal from './MusteriModal';


export default function Customers() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedPerson, setSelectedPerson] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Export hook
    const { exportToExcel, isExporting } = useExportExcel();

    const allCustomers = getCustomersByConstructionId(constructionId);
    const stats = getCustomerStats(constructionId);

    // Filter customers based on search query
    const customers = allCustomers.filter(person => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            person.name?.toLowerCase().includes(query) ||
            person.phone?.toLowerCase().includes(query) ||
            person.role?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const {
        paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        goToPage,
        resetPage,
    } = usePagination(customers, 10);

    // Reset pagination when search changes
    useEffect(() => {
        resetPage();
    }, [searchQuery]);


    const handleAdd = () => {
        setModalOpen(true);
    };

    const handleDelete = (person) => {
        setSelectedPerson(person);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        console.log('Müşteri silindi:', selectedPerson);
        toast.success(`${selectedPerson?.name} başarıyla silindi`);
        setDeleteModalOpen(false);
        setSelectedPerson(null);
    };

    const handleSubmit = (data) => {
        console.log('Müşteri eklendi:', data);
        toast.success(`${data.name} başarıyla eklendi`);
    };

    // Loading state - API entegrasyonunda true olarak başlatılacak
    const [isLoading] = useState(false);
    if (isLoading) return <TablePageSkeleton statsCount={4} tableRows={10} tableColumns={6} />;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Stats */}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CardStats
                    title="Toplam Müşteri"
                    value={stats.total}
                    icon={Users}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Alıcılar"
                    value={stats.customers}
                    icon={Users}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Arsa Sahipleri"
                    value={stats.landowners}
                    icon={Users}
                    color="warning"
                    size="sm"
                />
                <CardStats
                    title="Yatırımcılar"
                    value={stats.investors}
                    icon={Users}
                    color="info"
                    size="sm"
                />
            </div>

            {/* Table Header */}
            <TableHeader
                title="Müşteriler"
                subtitle={showAllConstructions ? 'Tüm inşaatlar' : selectedConstruction?.name}
            >
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="İsim, telefon veya rol ara..."
                    size="md"
                />
                <ExportDropdown
                    onExportExcel={() => exportToExcel({
                        data: customers,
                        columns: [
                            { header: 'Ad Soyad', key: 'name' },
                            { header: 'Telefon', key: 'phone' },
                            { header: 'Email', key: 'email' },
                            { header: 'Rol', key: 'role' },
                            { header: 'Daire', key: 'apartment' },
                        ],
                        fileName: 'musteriler_listesi',
                        sheetName: 'Müşteriler',
                    })}
                    isExporting={isExporting}
                    disabled={customers.length === 0}
                />
                <Button

                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={handleAdd}
                >
                    Müşteri Ekle
                </Button>
            </TableHeader>

            {/* Table */}
            <MusterilerTablosu data={paginatedData} onDelete={handleDelete} />

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={goToPage}
            />


            {/* Add Modal */}
            <MusteriModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
            />

            {/* Delete Confirm Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Müşteriyi Sil"
                message={`"${selectedPerson?.name}" müşterisini silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
                confirmText="Evet, Sil"
                variant="danger"
            />
        </div>
    );
}
